import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Rocket, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
    deployWorkflow,
    getDeploymentsByNameSlug,
    getRunningInstancesCount,
    deleteDeployment,
    updateWorkflow
} from '../../../services/workflowService';

interface DeployModalProps {
    isOpen: boolean;
    onClose: () => void;
    workflow: {
        id: string;
        nameSlug?: string;
        name: string;
        status: string;
        version?: string;
        definitionId?: string | null;
        definitionKey?: string | null;
    } | null;
    bpmnXml: string;
    onDeploySuccess: (definitionId: string, definitionKey: string) => void;
}

const DeployModal: React.FC<DeployModalProps> = ({
    isOpen,
    onClose,
    workflow,
    bpmnXml,
    onDeploySuccess
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [runningInstancesCount, setRunningInstancesCount] = useState(0);
    const [existingDeployments, setExistingDeployments] = useState<any[]>([]);
    const [selectedOption, setSelectedOption] = useState<'new' | 'replace'>('new');

    const isDeployed = workflow?.status === 'DEPLOYED';

    // Check deployment status when modal opens
    useEffect(() => {
        if (isOpen && workflow?.nameSlug) {
            checkDeploymentStatus();
        }
    }, [isOpen, workflow?.nameSlug]);

    const checkDeploymentStatus = async () => {
        if (!workflow?.nameSlug) return;

        setCheckingStatus(true);
        try {
            // Get existing deployments
            const deployments = await getDeploymentsByNameSlug(workflow.nameSlug);
            setExistingDeployments(deployments);

            // Get running instances count if we have a definition key
            if (workflow.definitionKey) {
                const count = await getRunningInstancesCount(workflow.definitionKey);
                setRunningInstancesCount(count);
            } else if (workflow.nameSlug) {
                // Try using nameSlug as key
                const count = await getRunningInstancesCount(workflow.nameSlug);
                setRunningInstancesCount(count);
            }
        } catch (error) {
            console.error('Error checking deployment status:', error);
        } finally {
            setCheckingStatus(false);
        }
    };

    const handleDeployNewVersion = async () => {
        if (!workflow?.nameSlug || !bpmnXml) return;

        setIsLoading(true);
        try {
            // Pass version to deployment for versioned deployment names
            const response = await deployWorkflow(workflow.nameSlug, bpmnXml, workflow.version);

            // Extract definitionId and definitionKey from response
            const deployedProcessDefinitions = response.deployedProcessDefinitions || {};
            const definitionId = Object.keys(deployedProcessDefinitions)[0] || '';
            const definitionKey = deployedProcessDefinitions[definitionId]?.key || workflow.nameSlug || '';

            // Update the workflow record with deployment info
            if (workflow.id && definitionId) {
                await updateWorkflow(workflow.id, {
                    status: 'DEPLOYED',
                    definitionId,
                    definitionKey
                });
            }

            toast.success('Workflow deployed successfully as new version');
            onDeploySuccess(definitionId, definitionKey);
            onClose();
        } catch (error) {
            console.error('Deploy failed:', error);
            toast.error('Failed to deploy workflow');
        } finally {
            setIsLoading(false);
        }
    };

    const handleReplaceDeployment = async () => {
        if (!workflow?.nameSlug || !bpmnXml) return;

        // Confirm with user about data loss
        if (runningInstancesCount > 0) {
            const confirmed = window.confirm(
                `⚠️ WARNING: There are ${runningInstancesCount} running instances.\n\n` +
                `Deleting the deployment will:\n` +
                `• Stop all running instances\n` +
                `• Delete all process history\n` +
                `• Remove all associated data\n\n` +
                `This action cannot be undone. Continue?`
            );
            if (!confirmed) return;
        }

        setIsLoading(true);
        try {
            // Delete all existing deployments
            for (const deployment of existingDeployments) {
                await deleteDeployment(deployment.id, true);
            }

            // Deploy new version with version in deployment name
            const response = await deployWorkflow(workflow.nameSlug, bpmnXml, workflow.version);

            // Extract definitionId and definitionKey from response
            const deployedProcessDefinitions = response.deployedProcessDefinitions || {};
            const definitionId = Object.keys(deployedProcessDefinitions)[0] || '';
            const definitionKey = deployedProcessDefinitions[definitionId]?.key || workflow.nameSlug || '';

            // Update the workflow record with deployment info
            if (workflow.id && definitionId) {
                await updateWorkflow(workflow.id, {
                    status: 'DEPLOYED',
                    definitionId,
                    definitionKey
                });
            }

            toast.success('Deployment replaced successfully');
            onDeploySuccess(definitionId, definitionKey);
            onClose();
        } catch (error) {
            console.error('Replace deployment failed:', error);
            toast.error('Failed to replace deployment');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeploy = () => {
        if (selectedOption === 'new') {
            handleDeployNewVersion();
        } else {
            handleReplaceDeployment();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-teal-100 rounded-lg">
                            <Rocket size={20} className="text-teal-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">Deploy Workflow</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4">
                    {checkingStatus ? (
                        <div className="flex items-center justify-center py-8">
                            <RefreshCw size={24} className="text-teal-500 animate-spin mr-3" />
                            <span className="text-gray-600">Checking deployment status...</span>
                        </div>
                    ) : (
                        <>
                            <p className="text-gray-600 mb-4">
                                Deploy <strong>{workflow?.name || 'this workflow'}</strong> to the process engine.
                            </p>

                            {!isDeployed ? (
                                // Not deployed - simple deploy
                                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-4">
                                    <p className="text-teal-800">
                                        This workflow has not been deployed yet. Click deploy to create the first deployment.
                                    </p>
                                </div>
                            ) : (
                                // Already deployed - show options
                                <>
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
                                            <div>
                                                <p className="text-amber-800 font-medium">This workflow is already deployed</p>
                                                <p className="text-amber-700 text-sm mt-1">
                                                    {existingDeployments.length} deployment(s) found
                                                    {runningInstancesCount > 0 && (
                                                        <span className="block mt-1 text-red-600 font-medium">
                                                            ⚠️ {runningInstancesCount} running instance(s)
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {/* Option 1: Deploy as new version */}
                                        <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${selectedOption === 'new' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
                                            }`}>
                                            <input
                                                type="radio"
                                                name="deployOption"
                                                checked={selectedOption === 'new'}
                                                onChange={() => setSelectedOption('new')}
                                                className="mt-1 accent-teal-600"
                                            />
                                            <div>
                                                <p className="font-medium text-gray-900">Deploy as new version</p>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    Creates a new version. Running instances will continue with the old version.
                                                </p>
                                            </div>
                                        </label>

                                        {/* Option 2: Replace deployment */}
                                        <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${selectedOption === 'replace' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                                            }`}>
                                            <input
                                                type="radio"
                                                name="deployOption"
                                                checked={selectedOption === 'replace'}
                                                onChange={() => setSelectedOption('replace')}
                                                className="mt-1 accent-red-600"
                                            />
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <Trash2 size={16} className="text-red-500" />
                                                    <p className="font-medium text-gray-900">Delete and redeploy</p>
                                                </div>
                                                <p className="text-sm text-red-600 mt-1">
                                                    ⚠️ This will delete all running instances and history. This action cannot be undone.
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDeploy}
                        disabled={isLoading || checkingStatus}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 flex items-center gap-2 ${selectedOption === 'replace' && isDeployed
                            ? 'bg-red-500 hover:bg-red-600'
                            : 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600'
                            }`}
                    >
                        {isLoading && <RefreshCw size={16} className="animate-spin" />}
                        {isLoading ? 'Deploying...' : (
                            isDeployed
                                ? selectedOption === 'new' ? 'Deploy New Version' : 'Delete & Redeploy'
                                : 'Deploy'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeployModal;
